import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useRealtimeCases } from '@/hooks/useRealtimeCases';
import { ChargeCapturePanel } from '@/components/ChargeCapturePanel';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Activity,
  Heart,
  Wind,
  Thermometer,
  User,
  Clock,
  Package,
  Pill,
  Wrench,
  FileText,
} from 'lucide-react';

interface OrCase {
  case_id: number;
  case_number: string;
  patient_name: string;
  patient_mrn: string;
  procedure_name: string;
  surgeon_name: string;
  anesthesiologist_name: string;
  scrub_tech_name: string;
  circulating_nurse_name: string;
  or_room: string;
  case_status: string;
  actual_start: string;
  incision_time: string | null;
  anesthesia_type: string;
  patient_position: string;
}

interface VitalSigns {
  vital_id: number;
  recorded_at: string;
  heart_rate: number;
  blood_pressure_systolic: number;
  blood_pressure_diastolic: number;
  oxygen_saturation: number;
  respiratory_rate: number;
  temperature: number;
  is_critical: boolean;
}

interface CaseEquipment {
  equipment_name: string;
  checked_out_at: string;
}

interface CaseSupply {
  supply_name: string;
  quantity_used: number;
  unit_of_measure: string;
  logged_at: string;
}

interface CaseMedication {
  medication_name: string;
  dose: number;
  dose_unit: string;
  route: string;
  administered_at: string;
  indication: string;
}

interface CaseEvent {
  event_description: string;
  event_timestamp: string;
  event_type: string;
}

export function CaseDetailRoute() {
  const { caseId } = useParams();
  const navigate = useNavigate();

  useRealtimeCases(
    caseId
      ? [['case', caseId], ['case-vitals', caseId], ['case-events', caseId]]
      : []
  );

  const { data: caseData } = useQuery({
    queryKey: ['case', caseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('or_cases')
        .select('*')
        .eq('case_id', caseId)
        .single();

      if (error) throw error;
      return data as OrCase;
    },
  });

  const { data: vitals = [] } = useQuery({
    queryKey: ['case-vitals', caseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('case_vitals')
        .select('*')
        .eq('case_id', caseId)
        .order('recorded_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data as VitalSigns[];
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  const { data: equipment = [] } = useQuery({
    queryKey: ['case-equipment', caseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('case_equipment')
        .select('*')
        .eq('case_id', caseId)
        .order('checked_out_at', { ascending: false });

      if (error) throw error;
      return data as CaseEquipment[];
    },
  });

  const { data: supplies = [] } = useQuery({
    queryKey: ['case-supplies', caseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('case_supplies')
        .select('*')
        .eq('case_id', caseId)
        .order('logged_at', { ascending: false });

      if (error) throw error;
      return data as CaseSupply[];
    },
  });

  const { data: medications = [] } = useQuery({
    queryKey: ['case-medications', caseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('case_medications')
        .select('*')
        .eq('case_id', caseId)
        .order('administered_at', { ascending: false});

      if (error) throw error;
      return data as CaseMedication[];
    },
  });

  const { data: events = [] } = useQuery({
    queryKey: ['case-events', caseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('case_events')
        .select('*')
        .eq('case_id', caseId)
        .order('event_timestamp', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data as CaseEvent[];
    },
  });

  const calculateElapsedTime = (startTime: string) => {
    const start = new Date(startTime);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - start.getTime()) / 1000 / 60);
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  if (!caseData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pt-gold"></div>
      </div>
    );
  }

  const latestVitals = vitals[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/live-cases')}
          className="p-2 hover:bg-pt-dark rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-pt-text-muted" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-pt-text">{caseData.or_room}</h1>
            <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-lg text-sm font-medium border border-green-500/30">
              {caseData.case_status.replace(/_/g, ' ').toUpperCase()}
            </span>
          </div>
          <p className="text-pt-text-muted">{caseData.case_number} • {caseData.procedure_name}</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-pt-dark rounded-lg border border-pt-border">
          <Clock className="w-5 h-5 text-pt-gold" />
          <div>
            <p className="text-xs text-pt-text-dim">Elapsed Time</p>
            <p className="text-lg font-semibold text-pt-text">
              {calculateElapsedTime(caseData.actual_start)}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Vitals & Patient Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Current Vitals */}
          {latestVitals && (
            <div className="bg-pt-dark rounded-lg border border-pt-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-pt-text flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-400" />
                  Current Vital Signs
                </h2>
                <span className="text-xs text-pt-text-dim">
                  {format(new Date(latestVitals.recorded_at), 'HH:mm:ss')}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-pt-darker/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-4 h-4 text-red-400" />
                    <span className="text-xs text-pt-text-muted">Heart Rate</span>
                  </div>
                  <p className="text-2xl font-bold text-pt-text">{latestVitals.heart_rate}</p>
                  <p className="text-xs text-pt-text-dim">bpm</p>
                </div>

                <div className="bg-pt-darker/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-4 h-4 text-pt-gold" />
                    <span className="text-xs text-pt-text-muted">BP</span>
                  </div>
                  <p className="text-2xl font-bold text-pt-text">
                    {latestVitals.blood_pressure_systolic}/{latestVitals.blood_pressure_diastolic}
                  </p>
                  <p className="text-xs text-pt-text-dim">mmHg</p>
                </div>

                <div className="bg-pt-darker/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wind className="w-4 h-4 text-cyan-400" />
                    <span className="text-xs text-pt-text-muted">SpO2</span>
                  </div>
                  <p className="text-2xl font-bold text-pt-text">{latestVitals.oxygen_saturation}</p>
                  <p className="text-xs text-pt-text-dim">%</p>
                </div>

                <div className="bg-pt-darker/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Thermometer className="w-4 h-4 text-orange-400" />
                    <span className="text-xs text-pt-text-muted">Temp</span>
                  </div>
                  <p className="text-2xl font-bold text-pt-text">{latestVitals.temperature}</p>
                  <p className="text-xs text-pt-text-dim">°C</p>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-pt-dark rounded-lg border border-pt-border p-6">
            <h2 className="text-lg font-semibold text-pt-text mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-pt-gold" />
              Case Timeline
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {events.map((event, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    {index < events.length - 1 && (
                      <div className="w-0.5 h-full bg-pt-panel mt-1"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-3">
                    <p className="text-sm text-pt-text">{event.event_description}</p>
                    <p className="text-xs text-pt-text-dim mt-1">
                      {format(new Date(event.event_timestamp), 'HH:mm:ss')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Team, Equipment, Supplies, Meds */}
        <div className="space-y-6">
          {/* Team */}
          <div className="bg-pt-dark rounded-lg border border-pt-border p-6">
            <h3 className="text-lg font-semibold text-pt-text mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-purple-400" />
              Team
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-pt-text-dim">Patient</p>
                <p className="text-pt-text">{caseData.patient_name}</p>
                <p className="text-xs text-pt-text-dim">MRN: {caseData.patient_mrn}</p>
              </div>
              <div>
                <p className="text-pt-text-dim">Surgeon</p>
                <p className="text-pt-text">{caseData.surgeon_name}</p>
              </div>
              <div>
                <p className="text-pt-text-dim">Anesthesiologist</p>
                <p className="text-pt-text">{caseData.anesthesiologist_name}</p>
              </div>
              <div>
                <p className="text-pt-text-dim">Scrub Tech</p>
                <p className="text-pt-text">{caseData.scrub_tech_name}</p>
              </div>
              <div>
                <p className="text-pt-text-dim">Circulating Nurse</p>
                <p className="text-pt-text">{caseData.circulating_nurse_name}</p>
              </div>
            </div>
          </div>

          {/* Equipment */}
          <div className="bg-pt-dark rounded-lg border border-pt-border p-6">
            <h3 className="text-lg font-semibold text-pt-text mb-4 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-yellow-400" />
              Equipment ({equipment.length})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {equipment.map((item, index) => (
                <div key={index} className="flex items-start gap-2 text-sm">
                  <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full mt-1.5"></div>
                  <div className="flex-1">
                    <p className="text-pt-text">{item.equipment_name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Supplies */}
          <div className="bg-pt-dark rounded-lg border border-pt-border p-6">
            <h3 className="text-lg font-semibold text-pt-text mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-cyan-400" />
              Supplies ({supplies.length})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {supplies.map((item, index) => (
                <div key={index} className="flex items-start justify-between text-sm">
                  <span className="text-pt-text">{item.supply_name}</span>
                  <span className="text-pt-text-dim">
                    {item.quantity_used} {item.unit_of_measure}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Medications */}
          <div className="bg-pt-dark rounded-lg border border-pt-border p-6">
            <h3 className="text-lg font-semibold text-pt-text mb-4 flex items-center gap-2">
              <Pill className="w-5 h-5 text-green-400" />
              Medications ({medications.length})
            </h3>
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {medications.map((item, index) => (
                <div key={index} className="text-sm">
                  <p className="text-pt-text font-medium">{item.medication_name}</p>
                  <p className="text-pt-text-dim text-xs">
                    {item.dose} {item.dose_unit} • {item.route}
                  </p>
                  {item.indication && (
                    <p className="text-pt-text-dim text-xs mt-1">{item.indication}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Revenue cycle: documented usage -> draft superbill (Forge Billing) */}
      {caseId && <ChargeCapturePanel caseId={Number(caseId)} />}
    </div>
  );
}
